/**
 * TTRPGReports — smart wrapper for the post-session TTRPG artifacts surface.
 *
 * Owns the React Query graph (visuals fetch + reports fetch + reveal
 * mutation), splits visuals into the four section buckets the view needs,
 * composes the master image list for the lightbox, and forwards everything
 * to TTRPGReportsView. The view at ./TTRPGReportsView/index.tsx is the dumb
 * half — no useCompanyApi, no useQuery, no useMutation, no notifications —
 * which makes it Storybookable in isolation.
 *
 * Phase 5.4 of the smart/dumb cleanup.
 */
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import { type ILightboxEntry, useImageLightbox } from "../../hooks/useImageLightbox"
import { fetchReports, fetchVisuals, revealArtifact } from "../../lib/api"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import type { IProps, IReports, IVisual } from "./interfaces"
import { TTRPGReportsView } from "./TTRPGReportsView"
import type { TVisibility } from "./TTRPGReportsView/interfaces"

/**
 * Step ids whose artifacts are batch pipeline images (bucketed by
 * `content.sourceType` into scenes / moments / portraits). The monolithic
 * `image-generation` step was split into three; older meetings still carry the
 * legacy id, so all four are accepted.
 */
const PIPELINE_IMAGE_STEP_IDS: ReadonlySet<string> = new Set([
  "image-scenes",
  "image-plaques",
  "image-portraits",
  "image-generation",
])

export function TTRPGReports({ meetingId, isDM }: IProps): React.JSX.Element | null {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  const visualsQuery = useCompanyQuery<IVisual[]>({
    key: (org) => queryKeys.ttrpgVisuals(org, meetingId),
    queryFn: (company) => fetchVisuals(company, meetingId),
  })

  const reportsQuery = useCompanyQuery<IReports>({
    key: (org) => queryKeys.ttrpgReports(org, meetingId),
    queryFn: (company) => fetchReports(company, meetingId) as Promise<IReports>,
  })

  const revealMutation = useCompanyMutation({
    mutationFn: (company, { artifactId, visibility }: { artifactId: number; visibility: TVisibility }) =>
      revealArtifact(company, meetingId, artifactId, visibility),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.ttrpgVisuals(orgId, meetingId) }),
    // Without this, a failed reveal/hide leaves the DM believing players can/can't
    // see a visual when the opposite is true — surface the failure.
    onError: (err) => notifyError(err),
  })

  const allVisuals = useMemo(() => visualsQuery.data ?? [], [visualsQuery.data])
  const reports = reportsQuery.data ?? { dmReport: null, playerReports: [], characterArcs: [] }

  // Pipeline image artifacts come from the three split image steps
  // (image-scenes / image-plaques / image-portraits) plus the legacy
  // image-generation step id on older meetings. Bucket those by sourceType;
  // everything else (live progressive-visual etc.) stays in "other".
  const pipelineVisuals = useMemo(() => allVisuals.filter((v) => PIPELINE_IMAGE_STEP_IDS.has(v.stepId)), [allVisuals])
  const otherVisuals = useMemo(() => allVisuals.filter((v) => !PIPELINE_IMAGE_STEP_IDS.has(v.stepId)), [allVisuals])

  const sceneVisuals = useMemo(
    () =>
      pipelineVisuals
        .filter((v) => v.content.sourceType === "scene")
        .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)),
    [pipelineVisuals],
  )
  const momentVisuals = useMemo(
    () =>
      pipelineVisuals
        .filter((v) => v.content.sourceType === "memorable_moment")
        .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)),
    [pipelineVisuals],
  )
  const characterVisuals = useMemo(
    () => pipelineVisuals.filter((v) => v.content.sourceType === "character"),
    [pipelineVisuals],
  )

  // Build the master image list for the lightbox in display order:
  // scenes → moments → characters → other (progressive-visual etc.)
  const allImages: ILightboxEntry[] = useMemo(
    () =>
      [...sceneVisuals, ...momentVisuals, ...characterVisuals, ...otherVisuals].map((v) => ({
        url: v.content.url,
        alt: v.content.title ?? v.content.characterName ?? v.content.prompt ?? i18n._(msg`Image`),
      })),
    [sceneVisuals, momentVisuals, characterVisuals, otherVisuals, i18n],
  )

  const { openLightbox, LightboxModal } = useImageLightbox()

  const onImageClick = useCallback((index: number): void => openLightbox(allImages, index), [openLightbox, allImages])

  // Offsets into allImages for each section
  const momentOffset = sceneVisuals.length
  const characterOffset = momentOffset + momentVisuals.length
  const otherOffset = characterOffset + characterVisuals.length

  const revealPending = useCallback(
    (id: number): boolean => revealMutation.isPending && revealMutation.variables?.artifactId === id,
    [revealMutation],
  )
  const onReveal = useCallback(
    (id: number, visibility: TVisibility): void => {
      revealMutation.mutate({ artifactId: id, visibility })
    },
    [revealMutation],
  )

  const totalImages = allImages.length
  const isLoading = visualsQuery.isLoading && reportsQuery.isLoading

  return (
    <TTRPGReportsView
      meetingId={meetingId}
      isDM={isDM}
      sceneVisuals={sceneVisuals}
      momentVisuals={momentVisuals}
      characterVisuals={characterVisuals}
      otherVisuals={otherVisuals}
      dmReport={reports.dmReport}
      playerReports={reports.playerReports}
      characterArcs={reports.characterArcs}
      totalImages={totalImages}
      onImageClick={onImageClick}
      momentOffset={momentOffset}
      characterOffset={characterOffset}
      otherOffset={otherOffset}
      isLoading={isLoading}
      revealPending={revealPending}
      onReveal={onReveal}
      lightboxModal={LightboxModal}
    />
  )
}
